import type React from "react";
import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useGridDefinitions } from "../../../../controls/ag-grid/components/GridDefinitions";
import { DelayAreaSelector } from "../../../../shared/selectors/DelayAreaSelector";
import { useCategoriesOptionsFunctions } from "../hooks/useCategoriesOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { DelayCategory } from "../models/CatalogCategory";
import { UpsertDeleteModal } from "./UpsertDeleteModal";

type Props = {
  categories: DelayCategory[];
  isLoading: boolean;
  assetId: number;
  setAssetId: (id: number) => void;
  includeInactives: boolean;
  setIncludeInactives: (include: boolean) => void;
};

export const TableCategories = ({
  categories,
  isLoading,
  assetId,
  setAssetId,
  includeInactives,
  setIncludeInactives,
}: Props) => {
  const [showModal, setShowModal] = useState<
    "create" | "update" | "delete" | ""
  >("");
  const [selectedRowData, setSelectedRowData] = useState<DelayCategory | null>(
    null,
  );
  const [error, setError] = useState("");

  const { showContextMenu, registerConfig } = useContextMenuMESF();
  const { getMenuOptions } = useCategoriesOptionsFunctions({
    setSelectedRowData,
    setShowModal,
  });
  const { columnDefs } = useTableData({
    setSelectedRowData,
    setShowModal,
    showContextMenu,
  });

  const { columnDefaults } = useGridDefinitions({
    OnEdit: (data: any) => {
      setShowModal("update");
      setSelectedRowData(data);
    },
  });

  const onRowDoubleClicked = (event: any) => {
    setSelectedRowData(event.data);
    setShowModal("update");
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      // ag-grid internally passes the DOM event but it's not in the public types
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;
      if (nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "categoriesContext",
        );
      }
      if (data) {
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [categories],
  );

  useEffect(() => {
    registerConfig({
      id: "categoriesContext",
      getOptions: getMenuOptions,
    });
  }, []);

  return (
    <>
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography
            variant="h5"
            style={{ margin: "20px 0 20px", fontWeight: 600 }}
          >
            Delays Categories
          </Typography>
          <Grid
            container
            item
            md={10}
            xs={12}
            spacing={2}
            justifyContent="flex-start"
            alignItems="center"
            style={{ padding: "8px 8px 8px 0" }}
          >
            <Grid item md={3} xs={6}>
              <DelayAreaSelector
                value={assetId}
                onChange={(id) => id !== null && setAssetId(id)}
              />
            </Grid>
            <Grid item style={{ width: "fit-content" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeInactives}
                    color="primary"
                    onChange={(e) => setIncludeInactives(!includeInactives)}
                  />
                }
                label="Inactive"
                style={{ marginBottom: 0, marginLeft: 1, width: "100%" }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container justifyContent="center" alignItems="center">
          <Grid
            item
            md={12}
            xs={12}
            style={{
              height: "61vh",
              padding: 10,
              margin: "20px 0 10px",
            }}
          >
            <div style={{ height: "100%", width: "100%" }}>
              <AgGridReact<DelayCategory>
                rowData={categories || []}
                columnDefs={columnDefs}
                defaultColDef={columnDefaults}
                rowHeight={34}
                headerHeight={28}
                animateRows
                rowSelection="single"
                getContextMenuItems={getContextMenuItems}
                onRowDoubleClicked={onRowDoubleClicked}
                loading={isLoading}
                getRowId={(params) => `${params.data.DelayCategoryId}`}
              />
            </div>
          </Grid>
          <Grid container justifyContent="flex-end">
            <Grid
              item
              md={3}
              sm={4}
              xs={12}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                variant="contained"
                color="primary"
                fullWidth
                style={{ margin: "0px 15px" }}
                onClick={() => {
                  setShowModal("create");
                }}
              >
                NEW CATEGORY
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <ErrorModal error={error} onHide={() => setError("")} />
      <UpsertDeleteModal
        delayCategory={selectedRowData}
        modal={showModal}
        onHide={() => setShowModal("")}
      />
    </>
  );
};
